// @ts-nocheck
// Flow model for the playground:
//  - GREEN buy spine: launch → wallet → wallet … (ordered buy flow). One buy-out per node.
//  - DOTTED rotate branch: wallet → new wallet (it exits and funds a fresh buyer).
//  - DOTTED sell branch: wallet → a Sell node, with a rule (now / +X% gain / after Ns).
// A wallet can have one buy-continuation AND one dotted branch (rotate or sell).
// Linearized into the Choreography tape for the simulator + executor.

import {
  simulate,
  uid,
  type Action,
  type Choreography,
  type SimResult,
  type Step as ChoreoStep,
  type TapeAction,
  type Wallet,
} from "@/lib/algo/choreography";

export type EdgeKind = "buy" | "rotate" | "airdrop";

export type GraphEdge = {
  id: string;
  source: string; // wallet id or "launch"
  target: string; // wallet id (buyer for buy; destination for rotate) or airdrop list id
  kind: EdgeKind;
  order: number;
  bundleId?: string;
  sol: number; // buy size
  waitSec?: number; // buy: seconds to wait before firing (default 2; ignored when bundled)
  sellPct: number; // rotate: % of source holdings to roll over
};

/** A wallet's exit: now = after the buy flow; pct = at +value% gain; time = value seconds after its buy. */
export type SellTrigger = "now" | "pct" | "time";
export type SellNode = {
  id: string;
  walletId: string; // the wallet that sells
  trigger: SellTrigger;
  value: number; // pct gain (e.g. 100) or seconds (time)
  pct: number; // how much of holdings to sell (1–100)
  x: number;
  y: number;
};

export type GraphWallet = Wallet & {
  enabled: boolean;
  x: number;
  y: number;
  publicKey?: string;
  loopIntervalMs?: number;
};

export type AirdropRecipient = { address: string; amount: number; pct: number };

export type AirdropList = {
  id: string;
  name: string;
  sourceMint?: string;
  mint?: string;
  recipients: AirdropRecipient[];
};

// mirror the engine (server/engine/launch.ts)
export type LaunchCoinType = "pump-sol" | "pump-usdc" | "cashback" | "charity" | "agent";
export type RewardMode = "creator" | "wallet" | "github" | "charity" | "split";
export type RewardSplit = { wallet: string; pct: number };

export type LaunchCfg = {
  name: string;
  symbol: string;
  imageDataUrl?: string;
  coinType: LaunchCoinType;
  devWalletId?: string;
  slippage: number;
  priorityFee: number;
  useJito: boolean;
  // metadata
  description?: string;
  website?: string;
  twitter?: string;
  telegram?: string;
  // creator-reward routing
  rewardMode: RewardMode;
  rewardWallet?: string;        // mode "wallet": destination address
  rewardGithubUserId?: string;  // mode "github"
  rewardCharityConfigId?: string; // mode "charity"
  rewardSplit?: RewardSplit[];  // mode "split": shareholders (pct → bps in executor)
};

export type FlowGraph = {
  name: string;
  devBuySol: number;
  solUsd: number;
  bucketMs: number;
  launch: LaunchCfg;
  wallets: GraphWallet[];
  edges: GraphEdge[];
  sells: SellNode[];
  airdropLists?: AirdropList[];
};

export const LAUNCH_NODE_ID = "launch";

export function defaultLaunchCfg(): LaunchCfg {
  return { name: "", symbol: "", coinType: "pump-sol", slippage: 25, priorityFee: 0.0001, useJito: false, rewardMode: "creator" };
}

export function walletLayoutPos(index: number): { x: number; y: number } {
  const perRow = 5;
  return { x: 60 + (index % perRow) * 220, y: 60 + Math.floor(index / perRow) * 200 };
}

export function defaultEdge(kind: EdgeKind, source: string, target: string, order: number): GraphEdge {
  return { id: uid("edge"), source, target, kind, order, sol: kind === "buy" ? 1 : 0, waitSec: kind === "buy" ? 2 : 0, sellPct: 100 };
}

export function defaultSell(walletId: string, x: number, y: number): SellNode {
  return { id: uid("sell"), walletId, trigger: "time", value: 30, pct: 100, x, y };
}

/** Hierarchical order labels: main buy spine = 1,2,3…; a rotate sub-branch off
 *  wallet N = N.1, then its continuation N.2, N.3… (nested deeper as N.1.1). */
export function walletLabels(graph: FlowGraph): Map<string, string> {
  const buyChild = new Map<string, string>();
  const rotChild = new Map<string, string>();
  for (const e of [...graph.edges].sort((a, b) => a.order - b.order)) {
    if (e.kind === "buy" && !buyChild.has(e.source)) buyChild.set(e.source, e.target);
    else if (e.kind === "rotate" && !rotChild.has(e.source)) rotChild.set(e.source, e.target);
  }
  const out = new Map<string, string>();
  const seen = new Set<string>([LAUNCH_NODE_ID]);
  const stack: { id: string; label: number[] }[] = [{ id: LAUNCH_NODE_ID, label: [0] }];
  while (stack.length) {
    const { id, label } = stack.pop()!;
    if (id !== LAUNCH_NODE_ID) out.set(id, label.join("."));
    const r = rotChild.get(id);
    if (r && !seen.has(r)) { seen.add(r); stack.push({ id: r, label: [...label, 1] }); }       // rotate → deeper level
    const b = buyChild.get(id);
    if (b && !seen.has(b)) { seen.add(b); stack.push({ id: b, label: [...label.slice(0, -1), label[label.length - 1] + 1] }); } // buy → same level +1
  }
  return out;
}

export function edgeLabel(e: GraphEdge): string {
  return e.kind === "buy" ? `buy ${e.sol} SOL` : "rotate"; // rotate is always instant 100% — no config
}

const ROTATE_GAP_MS = 2000; // rotate fires this long after its source bought (instant sell→buy, no config)

/** Build the LIVE TAPE from the playground: every trade with an ABSOLUTE timestamp.
 *  dev buy @0; each buy waits its own `waitSec` (0 when bundled — fires in the slot);
 *  a rotate fires ~2s after its source bought; a sell fires at its wallet's buy time
 *  + its delay. Candles derive strictly from this tape. */
export function buildTape(graph: FlowGraph): TapeAction[] {
  const enabled = new Set(graph.wallets.filter((w) => w.enabled).map((w) => w.id));
  const byId = new Map(graph.wallets.map((w) => [w.id, w] as const));
  const name = (id: string) => byId.get(id)?.label ?? id;
  const buyEdgeFrom = new Map<string, GraphEdge>();
  const rotEdgeFrom = new Map<string, GraphEdge>();
  for (const e of [...graph.edges].sort((a, b) => a.order - b.order)) {
    if (e.kind === "buy" && enabled.has(e.target) && !buyEdgeFrom.has(e.source)) buyEdgeFrom.set(e.source, e);
    else if (e.kind === "rotate" && enabled.has(e.source) && enabled.has(e.target) && !rotEdgeFrom.has(e.source)) rotEdgeFrom.set(e.source, e);
  }
  // entry time per wallet (when it acquires its position), walking from Launch.
  // buy hop = its own wait (0 if bundled); rotate hop = fixed gap.
  const entry = new Map<string, number>();
  const seen = new Set<string>([LAUNCH_NODE_ID]);
  const stack: { id: string; t: number }[] = [{ id: LAUNCH_NODE_ID, t: 0 }];
  while (stack.length) {
    const { id, t } = stack.pop()!;
    if (id !== LAUNCH_NODE_ID) entry.set(id, t);
    const r = rotEdgeFrom.get(id); if (r && !seen.has(r.target)) { seen.add(r.target); stack.push({ id: r.target, t: t + ROTATE_GAP_MS }); }
    const b = buyEdgeFrom.get(id); if (b && !seen.has(b.target)) { seen.add(b.target); stack.push({ id: b.target, t: t + (b.bundleId ? 0 : Math.max(0, b.waitSec ?? 2) * 1000) }); }
  }
  const tape: TapeAction[] = [];
  if (graph.devBuySol > 0) tape.push({ t: 0, kind: "buy", walletId: "creator", sol: graph.devBuySol, label: "dev" });
  for (const e of graph.edges) {
    if (e.kind === "buy" && enabled.has(e.target)) tape.push({ t: entry.get(e.target) ?? 0, kind: "buy", walletId: e.target, sol: e.sol, label: name(e.target) });
    else if (e.kind === "rotate" && enabled.has(e.source) && enabled.has(e.target)) tape.push({ t: entry.get(e.target) ?? 0, kind: "rotate", walletId: e.source, toWalletId: e.target, sellPct: e.sellPct, label: name(e.source), toTrader: name(e.target) });
    else if (e.kind === "airdrop" && (e.source === LAUNCH_NODE_ID || enabled.has(e.source))) {
      const baseT = e.source === LAUNCH_NODE_ID ? 0 : (entry.get(e.source) ?? 0);
      tape.push({ t: baseT + Math.max(0, e.waitSec ?? 2) * 1000, kind: "airdrop", walletId: e.source, toWalletId: e.target, sellPct: e.sellPct, label: e.source === LAUNCH_NODE_ID ? "dev" : name(e.source) });
    }
  }
  for (const s of graph.sells) {
    if (!enabled.has(s.walletId) || !(s.pct > 0) || s.trigger === "pct") continue;
    const base = entry.get(s.walletId) ?? 0;
    const t = base + (s.trigger === "now" ? 0 : Math.max(0, s.value) * 1000);
    tape.push({ t, kind: "sell", walletId: s.walletId, pct: s.pct, label: name(s.walletId) });
  }

  // Orthogonal Looping: Replicate all actions for wallets that have loopIntervalMs > 0
  const MAX_TIME = 5 * 60 * 1000; // 5 minutes max duration for preview
  const loopedWallets = graph.wallets.filter(w => w.enabled && w.loopIntervalMs && w.loopIntervalMs > 0);
  
  for (const w of loopedWallets) {
    // Extract the initial macro of actions for this wallet
    const macroActions = tape.filter(a => 
      (a.kind === "buy" && a.walletId === w.id) ||
      (a.kind === "sell" && a.walletId === w.id) ||
      (a.kind === "rotate" && a.walletId === w.id)
    );
    
    if (macroActions.length === 0) continue;
    
    let iters = 1;
    while (iters < 500) {
      const offsetMs = iters * w.loopIntervalMs!;
      let anyValid = false;
      
      for (const a of macroActions) {
        const newT = a.t + offsetMs;
        if (newT <= MAX_TIME) {
          anyValid = true;
          tape.push({ ...a, t: newT });
        }
      }
      
      if (!anyValid) break; // all actions in this iteration are past MAX_TIME
      iters++;
    }
  }

  const rank = (k: TapeAction["kind"]) => (k === "buy" ? 0 : k === "rotate" ? 1 : 2);
  return tape.sort((a, b) => a.t - b.t || rank(a.kind) - rank(b.kind));
}

export function sellLabel(s: SellNode): string {
  const amt = `${s.pct}%`;
  if (s.trigger === "now") return `sell ${amt}`;
  if (s.trigger === "pct") return `sell ${amt} @ +${s.value}% · sim-only`; // price triggers don't execute live
  return `sell ${amt} @ ${s.value}s`;
}

/** Group consecutive same-bundleId buy/rotate edges (by order); map to the Choreography tape. */
export function graphToChoreography(graph: FlowGraph): Choreography {
  const enabled = new Set(graph.wallets.filter((w) => w.enabled).map((w) => w.id));
  const usable = graph.edges
    .filter((e) => enabled.has(e.target) && (e.kind !== "rotate" || enabled.has(e.source)))
    .slice()
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));

  const choreoSteps: ChoreoStep[] = [];
  let i = 0;
  while (i < usable.length) {
    const e = usable[i];
    if (e.bundleId) {
      const run: GraphEdge[] = [];
      const bid = e.bundleId;
      while (i < usable.length && usable[i].bundleId === bid) { run.push(usable[i]); i += 1; }
      choreoSteps.push({ id: uid("cs"), label: `Bundle · ${run.length}`, sendMode: "bundle", gapMs: 1000, actions: run.map(toAction) });
    } else {
      choreoSteps.push({ id: uid("cs"), label: edgeLabel(e), sendMode: "normal", gapMs: 1500, actions: [toAction(e)] });
      i += 1;
    }
  }

  // sells fire AFTER the buy flow: "now"/"time" at their delay (now = 0s, time = value s);
  // "pct" are price-target take-profits fired mid-sim. The chart reflects real timing.
  const liveSells = graph.sells.filter((s) => enabled.has(s.walletId) && s.pct > 0);

  return {
    name: graph.name,
    devBuySol: graph.devBuySol,
    solUsd: graph.solUsd,
    bucketMs: graph.bucketMs,
    wallets: graph.wallets.filter((w) => w.enabled).map(({ id, label, group, fundingSol, loopIntervalMs }) => ({ id, label, group, fundingSol, loopIntervalMs })),
    steps: choreoSteps,
    tape: buildTape(graph),
    timedSells: liveSells
      .filter((s) => s.trigger === "now" || s.trigger === "time")
      .map((s) => ({ walletId: s.walletId, atMs: s.trigger === "now" ? 0 : Math.max(0, s.value) * 1000, pct: s.pct })),
    sellTargets: liveSells
      .filter((s) => s.trigger === "pct")
      .map((s) => ({ walletId: s.walletId, x: 1 + s.value / 100, pct: s.pct })),
  };
}

function toAction(e: GraphEdge): Action {
  if (e.kind === "buy") return { id: e.id, kind: "buy", walletId: e.target, sol: e.sol };
  return { id: e.id, kind: "rotate", walletId: e.source, toWalletId: e.target, sellPct: e.sellPct };
}

export function runGraph(graph: FlowGraph): SimResult {
  return simulate(graphToChoreography(graph));
}

const fin = (v: unknown, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const str = (v: unknown, d: string) => (typeof v === "string" ? v : d);
const oneOf = <T extends string>(v: unknown, allowed: readonly T[], d: T): T => (typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : d);

/** Validate/normalize an untrusted loaded JSON into a safe FlowGraph: drop
 *  dangling edges, sells on missing wallets, dedupe ids, coerce NaN. Never throws. */
export function normalizeLoadedGraph(raw: unknown, liveSolUsd: number): FlowGraph {
  const p = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const base = makeSampleGraph();
  const wIds = new Set<string>();
  const wallets: GraphWallet[] = (Array.isArray(p.wallets) ? p.wallets : []).flatMap((w: any) => {
    if (!w || typeof w.id !== "string" || wIds.has(w.id)) return [];
    wIds.add(w.id);
    return [{ id: w.id, label: str(w.label, "Wallet"), group: str(w.group, "core"), fundingSol: fin(w.fundingSol), enabled: w.enabled !== false, x: fin(w.x), y: fin(w.y), publicKey: typeof w.publicKey === "string" ? w.publicKey : undefined, loopIntervalMs: w.loopIntervalMs ? fin(w.loopIntervalMs) : undefined }];
  });
  const eIds = new Set<string>();
  const edges: GraphEdge[] = (Array.isArray(p.edges) ? p.edges : []).flatMap((e: any, i: number) => {
    if (!e || typeof e.source !== "string" || typeof e.target !== "string") return [];
    // If it's an airdrop, we don't strictly require the target to be in wIds (it could be an airdrop list id)
    if (e.kind !== "airdrop" && !wIds.has(e.target)) return [];
    if (e.source !== LAUNCH_NODE_ID && !wIds.has(e.source)) return [];
    const id = typeof e.id === "string" && !eIds.has(e.id) ? e.id : uid("edge");
    eIds.add(id);
    const kind = e.kind === "rotate" || e.kind === "airdrop" ? e.kind : "buy";
    return [{ id, source: e.source, target: e.target, kind, order: fin(e.order, i), bundleId: typeof e.bundleId === "string" ? e.bundleId : undefined, sol: fin(e.sol), waitSec: fin(e.waitSec, 2), sellPct: fin(e.sellPct, 100) }];
  });
  const sIds = new Set<string>();
  const sells: SellNode[] = (Array.isArray(p.sells) ? p.sells : []).flatMap((s: any) => {
    if (!s || !wIds.has(s.walletId)) return [];
    const id = typeof s.id === "string" && !sIds.has(s.id) ? s.id : uid("sell");
    sIds.add(id);
    const trigger: SellTrigger = s.trigger === "pct" || s.trigger === "time" ? s.trigger : "now";
    return [{ id, walletId: s.walletId, trigger, value: fin(s.value), pct: fin(s.pct, 100), x: fin(s.x), y: fin(s.y) }];
  });
  const lc = (p.launch && typeof p.launch === "object" ? p.launch : {}) as Record<string, unknown>;
  return {
    name: str(p.name, base.name),
    devBuySol: fin(p.devBuySol),
    solUsd: liveSolUsd > 0 ? liveSolUsd : base.solUsd,
    bucketMs: fin(p.bucketMs, 1000) > 0 ? fin(p.bucketMs, 1000) : 1000,
    launch: {
      ...defaultLaunchCfg(),
      name: str(lc.name, ""), symbol: str(lc.symbol, ""),
      coinType: oneOf<LaunchCoinType>(lc.coinType, ["pump-sol", "pump-usdc", "cashback", "charity", "agent"], "pump-sol"),
      devWalletId: typeof lc.devWalletId === "string" ? lc.devWalletId : undefined,
      slippage: fin(lc.slippage, 25), priorityFee: fin(lc.priorityFee, 0.0001), useJito: lc.useJito === true,
      imageDataUrl: typeof lc.imageDataUrl === "string" ? lc.imageDataUrl : undefined,
      description: str(lc.description, ""), website: str(lc.website, ""), twitter: str(lc.twitter, ""), telegram: str(lc.telegram, ""),
      rewardMode: oneOf<RewardMode>(lc.rewardMode, ["creator", "wallet", "github", "charity", "split"], "creator"),
      rewardWallet: str(lc.rewardWallet, ""), rewardGithubUserId: str(lc.rewardGithubUserId, ""), rewardCharityConfigId: str(lc.rewardCharityConfigId, ""),
      rewardSplit: Array.isArray(lc.rewardSplit) ? lc.rewardSplit.filter((s: unknown): s is RewardSplit => !!s && typeof (s as RewardSplit).wallet === "string").map((s: RewardSplit) => ({ wallet: s.wallet, pct: fin(s.pct) })) : [],
    },
    wallets, edges, sells,
    airdropLists: (Array.isArray(p.airdropLists) ? p.airdropLists : []).map((a: any) => ({
      id: typeof a.id === "string" ? a.id : uid("al"),
      name: typeof a.name === "string" ? a.name : "Airdrop List",
      sourceMint: typeof a.sourceMint === "string" ? a.sourceMint : undefined,
      recipients: Array.isArray(a.recipients) ? a.recipients.map((r: any) => ({
        address: typeof r.address === "string" ? r.address : "",
        amount: fin(r.amount, 0),
        pct: fin(r.pct, 0)
      })) : []
    }))
  };
}

export function makeSampleGraph(): FlowGraph {
  const wallets: GraphWallet[] = [];
  const edges: GraphEdge[] = [];
  const sells: SellNode[] = [];
  
  // Create 20 wallets in a 5-column grid
  for (let i = 0; i < 20; i++) {
    const row = Math.floor(i / 5);
    const col = i % 5;
    wallets.push({
      id: `w${i + 1}`,
      label: `Alpha-${i + 1}`,
      group: "core",
      fundingSol: 0, // Auto-computed by walletFundNeeds in the workspace
      enabled: true,
      x: 60 + col * 250,
      y: 60 + row * 260,
    });
  }

  // Bundle 1: Jito Sniper Bundle (Launch + 4 early buyers)
  const b1 = "bundle-launch";
  edges.push({ id: uid("edge"), source: LAUNCH_NODE_ID, target: "w1", kind: "buy", order: 0, sol: 2.0, waitSec: 0, bundleId: b1, sellPct: 100 });
  edges.push({ id: uid("edge"), source: "w1", target: "w2", kind: "buy", order: 1, sol: 1.5, waitSec: 0, bundleId: b1, sellPct: 100 });
  edges.push({ id: uid("edge"), source: "w2", target: "w3", kind: "buy", order: 2, sol: 1.0, waitSec: 0, bundleId: b1, sellPct: 100 });
  edges.push({ id: uid("edge"), source: "w3", target: "w4", kind: "buy", order: 3, sol: 0.5, waitSec: 0, bundleId: b1, sellPct: 100 });

  // Bundle 2: Fast-follow bundle (Wait 2s after launch bundle)
  const b2 = "bundle-follow";
  edges.push({ id: uid("edge"), source: "w4", target: "w5", kind: "buy", order: 4, sol: 1.0, waitSec: 2, bundleId: b2, sellPct: 100 });
  edges.push({ id: uid("edge"), source: "w5", target: "w6", kind: "buy", order: 5, sol: 0.8, waitSec: 0, bundleId: b2, sellPct: 100 });

  // Staggered algorithmic spine (Wallets 7 to 12)
  edges.push({ id: uid("edge"), source: "w6", target: "w7", kind: "buy", order: 6, sol: 0.5, waitSec: 3, sellPct: 100 });
  edges.push({ id: uid("edge"), source: "w7", target: "w8", kind: "buy", order: 7, sol: 0.5, waitSec: 2, sellPct: 100 });
  edges.push({ id: uid("edge"), source: "w8", target: "w9", kind: "buy", order: 8, sol: 0.25, waitSec: 5, sellPct: 100 });
  edges.push({ id: uid("edge"), source: "w9", target: "w10", kind: "buy", order: 9, sol: 1.0, waitSec: 10, sellPct: 100 });
  edges.push({ id: uid("edge"), source: "w10", target: "w11", kind: "buy", order: 10, sol: 0.3, waitSec: 4, sellPct: 100 });
  edges.push({ id: uid("edge"), source: "w11", target: "w12", kind: "buy", order: 11, sol: 0.2, waitSec: 2, sellPct: 100 });

  // Rotations: Wallets exiting their position and funding fresh wallets to obscure tracks
  edges.push({ id: uid("edge"), source: "w2", target: "w13", kind: "rotate", order: 12, sol: 0, waitSec: 0, sellPct: 100 });
  edges.push({ id: uid("edge"), source: "w13", target: "w14", kind: "buy", order: 13, sol: 1.2, waitSec: 3, sellPct: 100 });

  edges.push({ id: uid("edge"), source: "w5", target: "w15", kind: "rotate", order: 14, sol: 0, waitSec: 0, sellPct: 50 }); // rotate 50% out
  edges.push({ id: uid("edge"), source: "w15", target: "w16", kind: "buy", order: 15, sol: 0.5, waitSec: 5, sellPct: 100 });

  edges.push({ id: uid("edge"), source: "w9", target: "w17", kind: "rotate", order: 16, sol: 0, waitSec: 0, sellPct: 100 });
  edges.push({ id: uid("edge"), source: "w17", target: "w18", kind: "buy", order: 17, sol: 0.8, waitSec: 1, sellPct: 100 });

  edges.push({ id: uid("edge"), source: "w12", target: "w19", kind: "rotate", order: 18, sol: 0, waitSec: 0, sellPct: 100 });
  edges.push({ id: uid("edge"), source: "w19", target: "w20", kind: "buy", order: 19, sol: 0.1, waitSec: 2, sellPct: 100 });

  // Strategic Sells
  const addSell = (wId: string, trigger: "now" | "pct" | "time", value: number, pct: number, yOff = 120) => {
    const w = wallets.find(w => w.id === wId)!;
    // slightly offset multiple sells on same wallet if needed, but here we just place them below the wallet
    const existing = sells.filter(s => s.walletId === wId).length;
    sells.push({ id: uid("sell"), walletId: wId, trigger, value, pct, x: w.x, y: w.y + yOff + (existing * 60) });
  };

  // Early dumpers
  addSell("w1", "pct", 100, 50); // Sell 50% at +100% gain
  addSell("w1", "time", 300, 100); // Sell rest after 5m
  addSell("w3", "time", 45, 100);
  addSell("w4", "time", 60, 100);

  // Mid-term holds
  addSell("w6", "pct", 200, 100); // Moonbag target
  addSell("w7", "time", 180, 100);
  addSell("w8", "time", 240, 100);

  // Rotated wallet sells
  addSell("w14", "time", 600, 100);
  addSell("w16", "time", 900, 100);
  addSell("w18", "pct", 50, 100); // Quick flip on rotation

  const launch = defaultLaunchCfg();
  launch.useJito = true;
  launch.priorityFee = 0.005; // higher priority for a complex launch
  launch.name = "Citadel Alpha";
  launch.symbol = "CTDL";
  launch.description = "A complex multi-wallet algorithmic deployment demonstrating staggered entries, Jito bundling, rotations, and distributed profit taking.";

  return { 
    name: "Complex Citadel Deploy", 
    devBuySol: 1.5, 
    solUsd: 150, 
    bucketMs: 1000, 
    launch, 
    wallets, 
    edges, 
    sells,
    airdropLists: [] 
  };
}
